// The year page's sidebar state (YearSidebar/; Seán, 2026-10-22), all in the hash, so a reload,
// Back and links show the same: `#<panel>[&<item>][&details[-<person>]][&highlight=<slug>]
// [&autoplay=1|0]`. The panel (faq, highlights, testimonials) and its item: the FAQ answer open,
// the highlight shown (#highlights&jan-willem-tulp), the testimonial; a highlight's speaker dialog
// open (&details, &details-<person> in a talk of two); Highlights' autoplay wanted or not
// (&autoplay=1|0), kept on every panel; and on another panel the highlight to come back to
// (&highlight=<slug>; on Highlights the item is it, never both). The old "-" (#faq-videos) still
// read. No panel and no state (or another hash, e.g. #sign-in) is the page. On a wide screen the
// panel sits beside the page, Highlights first; on a narrow one it takes the page's place.
import {computed} from "vue";
import {useRoute, useRouter} from "vue-router";

export const PANELS = ["highlights", "program", "faq", "testimonials"];
const PANEL = /^(faq|highlights|program|testimonials)(?:-([a-z0-9-]+))?$/; // "faq", the old "faq-videos"
const PARAM = /^(highlight|autoplay)=([a-z0-9-]+)$/;
const DETAILS = /^details(?:-[a-z0-9-]+)?$/;
const ITEM = /^[a-z0-9-]+$/;
const EMPTY = {panel: "", answer: "", details: "", highlight: "", autoplay: ""};

// a hash's part after the panel, into the state; false when it isn't the sidebar's
function addPart(state, part) {
  const param = PARAM.exec(part);
  if (param) return Boolean((state[param[1]] = param[2]));
  if (!state.panel) return false;
  if (DETAILS.test(part)) return Boolean((state.details = part));
  if (!ITEM.test(part) || state.answer || state.details) return false;
  return Boolean((state.answer = part));
}

// "#faq&videos&autoplay=1" → {panel: "faq", answer: "videos", details: "", highlight: "",
// autoplay: "1"} (answer: the panel's item, whichever panel); isSidebar: false for another hash
export function getSidebarState(hash) {
  const parts = String(hash).replace(/^#/, "").split("&");
  const panel = PANEL.exec(parts[0]);
  const state = {...EMPTY, panel: panel?.[1] || "", answer: panel?.[2] || ""};
  const isRead = (panel ? parts.slice(1) : parts).every((part) => addPart(state, part));
  const isSidebar = isRead && Boolean(state.panel || state.highlight || state.autoplay);
  return isSidebar ? {...state, isSidebar} : {...EMPTY, isSidebar: false};
}
export const getIsSidebarHash = (hash) => getSidebarState(hash).isSidebar;

// the state into a hash: "" when there's none (the page)
export function getHash({panel, item, details, highlight, autoplay}) {
  const params = [highlight && `highlight=${highlight}`, autoplay && `autoplay=${autoplay}`];
  const parts = [panel, item, details, ...params].filter(Boolean);
  return parts.length ? `#${parts.join("&")}` : "";
}

// a name as an address's item: "Seán O'Donoghue & Christian Stolte" → "sean-odonoghue-christian-
// stolte" (accents dropped, apostrophes too)
export function getSlug(name) {
  const plain = String(name).normalize("NFKD").replace(/[̀-ͯ']/g, "").toLowerCase();
  return plain.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const getIsReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// autoplay wanted (Seán's autoplay spec, 2026-10-22): the hash's (&autoplay=1|0); else off for a
// link to one highlight (it shows that one, unmoving) or for reduced motion; else on
function getIsAutoplayRequested(autoplay, highlight) {
  if (autoplay === "1" || autoplay === "0") return autoplay === "1";
  return !highlight && !getIsReducedMotion();
}

export default function useYearSidebar() {
  const route = useRoute();
  const router = useRouter();
  const state = computed(() => getSidebarState(route.hash));
  // the highlight shown: Highlights' item, else the one kept for it (&highlight=)
  const highlight = computed(() => {
    const isHighlights = state.value.panel === "highlights";
    return (isHighlights && state.value.answer) || state.value.highlight;
  });
  const isAutoplayRequested = computed(() => {
    return getIsAutoplayRequested(state.value.autoplay, highlight.value);
  });
  // a panel's hash, Highlights' state kept: its highlight (as the item on Highlights, else
  // &highlight=) and &autoplay=
  function getPanelHash(panel, item = "") {
    const isHighlights = panel === "highlights";
    const kept = {highlight: isHighlights ? "" : highlight.value, autoplay: state.value.autoplay};
    return getHash({panel, item: isHighlights ? item || highlight.value : item, ...kept});
  }
  // the path as it is ("/2026/" stays so: without it, the router gave "/2026", a new page key,
  // and the whole page, with the focus, was rebuilt; 2026-10-22)
  const getAddress = (hash) => ({path: route.path, query: route.query, hash});
  // the panel chosen ("" the page): a new entry, so Back returns
  const choose = (panel) => router.push(getAddress(getPanelHash(panel)));
  // an item shown (an answer opened or closed): in place, no new entry each time, so Back still
  // leaves the panel
  const showItem = (panel, item) => router.replace(getAddress(getPanelHash(panel, item)));
  const openAnswer = (answer) => showItem("faq", answer);
  // a highlight shown, autoplay wanted or not: in place (no history entry: an automatic step adds
  // none)
  const setHighlights = ({slug = highlight.value, isAutoplay = isAutoplayRequested.value}) => {
    const hash = getHash({panel: "highlights", item: slug, autoplay: isAutoplay ? "1" : "0"});
    router.replace(getAddress(hash));
  };
  return {panel: computed(() => state.value.panel), answer: computed(() => state.value.answer),
    details: computed(() => state.value.details), choose, openAnswer, showItem, highlight,
    isAutoplayRequested, setHighlights, getPanelHash};
}
